// ConfluenceImportPage.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  getAllConfluencePages,
  getAllConfluenceSpaces,
  confluenceContentAnalysis,
  modifyBugData,
} from "../../../../services/webServices";

import ConfluencePagesComponentPage from "../../../common-components/ConfluencePagesComponentPage";
import LoadingModal from "../../../common-components/LoadingModal";
import ConfluenceSpacesComponentPage from "../../../common-components/ConfluenceSpacesComponentPage ";
import { handleProgressBar } from "../../../custom-components/ProgressBar/ProgressBarWithAPI";
import { LoadingModalContext, MessagePopupContext } from "../../ServicePage";

const ConfluenceImportPage = ({
  setConfluencePageOpen,
  issueType,
  setProceedingApp,
}) => {
  const navigate = useNavigate();

  const [spacesList, setSpacesList] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);

  const [pagesList, setPagesList] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);

  const [importLoading, setImportLoading] = useState(null);
  const [loader, setLoader] = useState(false);

  const { setLoadingState } = useContext(LoadingModalContext);
  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);

  // Prevent double-fetch in StrictMode
  const didFetch = useRef(false);
  // Fetch spaces
  useEffect(() => {
    if (didFetch.current) return; // skip if already fetched
    didFetch.current = true;
    const fetchSpaces = async () => {
      setImportLoading("Spaces");
      try {
        const response = await getAllConfluenceSpaces();
        setSpacesList(response.data);
      } catch (error) {
        console.error("Error fetching spaces:", error);
        const errMsg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch spaces. Please try again.";
        setMessage(errMsg);
        setMessagePopup(true);
      } finally {
        setImportLoading(null);
      }
    };
    fetchSpaces();
  }, []);

  // Fetch pages for selected space
  useEffect(() => {
    const fetchPages = async () => {
      setImportLoading("Pages");
      try {
        const response = await getAllConfluencePages({
          spaceKey: selectedSpace.key,
        });
        setPagesList(response.data);
      } catch (error) {
        console.error("Error fetching pages:", error);
        const errMsg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch pages. Please try again.";
        setMessage(errMsg);
        setMessagePopup(true);
      } finally {
        setImportLoading(null);
      }
    };

    if (selectedSpace) {
      fetchPages();
    } else {
      setSelectedPage(null);
      setPagesList([]);
    }
  }, [selectedSpace]);

  // Handle page selection (validation + navigation)
  const handlePageSelected = async (pageItem) => {
    let progressHandler;

    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 12000,
      });
      progressHandler.start("Validating bug details");
      const parser = new DOMParser();
      const doc = parser.parseFromString(pageItem.body, "text/html");
      const textContent = doc.body.textContent || "";

      const jsonData = JSON.stringify({ content: textContent });

      // Step 1: Validate
      const validBugDetails = await confluenceContentAnalysis(jsonData);
      const status = validBugDetails.data?.Status;
      const isValid =
        status?.trim().toLowerCase() === "yes, this is a bug candidate";
      if (!isValid) {
        progressHandler.stop();

        setMessage(
          "The selected page does not appear to contain valid bug details. Please choose another page."
        );
        setMessagePopup(true);
        setConfluencePageOpen(false);

        return;
      }

      progressHandler.updateMessage("Extracting bug details");

      // Modify bug data
      const bugData = await modifyBugData(jsonData);
      const mappedBugData = bugData.data.map((item) => ({
        title: item["Title"] || "",
        description: item["Description"] || "",
        environment: item["Environment Details"] || "",
        priority: item["Priority"] || "",
        stepsToReproduce: item["Steps to Reproduce"] || [],
        expectedResult: item["Expected Result"] || "",
        actualResult: item["Actual Result"] || "",
        additionalInfo: item["Additional Information"] || [],
        impact: item["Impact"] || "",
      }));

      await progressHandler.finishAndClose();

      // Navigate
      navigate("/dashboard/multi-bugreports/input", {
        state: {
          bugDetails: mappedBugData,
          confluenceConfig: {
            issueType,
            toolName: "Confluence",
            pageId: pageItem.id,
            pageTitle: pageItem.title,
            spaceKey: selectedSpace.key,
          },
        },
      });

      setConfluencePageOpen(false);
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.Error ||
        error?.response?.data?.Reason ||
        "Something went wrong. Please try again.";

      setMessage(errorMsg);
      setMessagePopup(true);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  return (
    <div>
      {/* Select Space */}
      {!selectedSpace && (
        <ConfluenceSpacesComponentPage
          issueType={issueType}
          taskType="Import"
          spacesLoading={importLoading === "Spaces"}
          setConfluencePageOpen={setConfluencePageOpen}
          selectedSpace={selectedSpace}
          setSelectedSpace={setSelectedSpace}
          spacesList={spacesList}
          setPreviousSelection={setProceedingApp}
        />
      )}

      {/* Select Page */}
      {selectedSpace && !selectedPage && (
        <ConfluencePagesComponentPage
          taskType="Import"
          pagesLoading={importLoading === "Pages"}
          pagesList={pagesList}
          setPagesList={setPagesList}
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          setImportPageOpen={setConfluencePageOpen}
          setSelectedSpace={setSelectedSpace}
          onPageSelected={handlePageSelected}
        />
      )}

      {loader && <LoadingModal open={loader} />}
    </div>
  );
};

export default ConfluenceImportPage;
